import React from 'react';

import Button, { ButtonGroup, LoadingButton } from '@atlaskit/button';
import SectionMessage from '@atlaskit/section-message';
import TextArea from '@atlaskit/textarea';
import TextField from '@atlaskit/textfield';
import { withFormik } from 'formik';

import { trackEvent } from 'src/lib/analytics';
import integration from 'src/lib/api/integration';
import jira from 'src/lib/api/jira';
import pd from 'src/lib/api/pagerduty';
import { textifyError } from 'src/lib/error';

import ErrorMessage from 'src/views/components/ErrorMessage';
import Field from 'src/views/components/Form/Field';

import SelectServicesForIncidentCreation from './SelectServicesForIncidentCreation';

const analyticsEvent = 'sidebar_new_incident';

function closeModal(e) {
  e.preventDefault();
  jira.closeCurrentModal();
}

function stripHtml(html) {
  if (!html) return '';
  try {
    return (new DOMParser())
      .parseFromString(html, 'text/html')
      .documentElement
      .textContent;
  } catch (e) {
    console.error(`Error while parsing description:`, e);
    return '';
  }
}

const CreateIncidentForm = (
  {
    values,
    touched,
    errors,
    handleChange,
    handleBlur,
    handleSubmit,
    isSubmitting,
    status,
    session,
  }) => {
  const notConnectedService = values.service && !values.service.associatedWithCurrentProject
    && 'Selected service is not linked to current Jira Project. Automation rules and mappings will not apply';

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexFlow: 'column nowrap', minHeight: '415px' }}>
      <Field label="Incident Title" isRequired errorMessage={touched.title && errors.title}>
        <TextField
          value={values.title}
          onChange={handleChange}
          onBlur={handleBlur}
          name="title"
        />
      </Field>

      <Field name="service" label="PagerDuty Service" isRequired errorMessage={touched.service && errors.service} helperMessage={notConnectedService}>
        <SelectServicesForIncidentCreation
          allowAllServices={session.loggedIn}
          value={values.service}
          onChange={handleChange}
          name="service"
        />
      </Field>

      <Field name="description" label="Incident description" errorMessage={touched.description && errors.description}>
        <TextArea
          minimumRows={5}
          maxHeight="300px"
          onChange={handleChange}
          value={values.description}
          onBlur={handleBlur}
          name="description"
        />
      </Field>

      {!isSubmitting && status && status.error && <div style={{ marginTop: 24 }}>
        <ErrorMessage error={status.error}/>
      </div>}

      {!isSubmitting && status && status.warning && <div style={{ marginTop: 24 }}>
        <SectionMessage appearance={'warning'}>
          {textifyError(status.warning)}
        </SectionMessage>
      </div>}

      <div style={{ flexGrow: 1 }}/>

      <div style={{ paddingTop: 24, paddingBottom: 24, display: 'flex', justifyContent: 'flex-end' }}>
        <ButtonGroup>
          <Button onClick={closeModal} isDisabled={Boolean(status && status.warning)}>Cancel</Button>
          <LoadingButton type="submit" appearance="primary" isLoading={isSubmitting} isDisabled={Boolean(status && status.warning)}>Create incident</LoadingButton>
          {status && Boolean(status.warning) && <Button appearance="primary" onClick={closeModal}>Close</Button>}
        </ButtonGroup>
      </div>
    </form>
  );
};
const createIncident = (service, title, description, issue, user) => {
  if (service.fromService) {
    return integration
      .createIncident(service.value, title, description, issue, user)
      .then(({ incident: { id: incidentId } }) => incidentId);
  } else if (service.associatedWithCurrentProject) {
    return pd
      .createIncidentViaLegacySidebarApi(service.value, title, description, issue, user.name)
      .then(({ incidentId }) => incidentId);
  }
  return pd
    .createIncident({
      title,
      service: {
        type: 'service_reference',
        id: service.value
      },
      body: {
        type: 'incident_body',
        details: description,
      }
    })
    .then(({ incident: { id } = {} }) => id);
};

const putIncidentIdIntoIssueProps = (issue, incidentId) => Promise.all([
  jira.setIssueProperty(issue.key, 'pagerdutyIncident', { connected: true }), // pagerdutyIncident is used by v3 integration backend
  jira.setIssueProperty(issue.key, 'pagerduty', { incidentId }) //pagerduty.incidentId is used by this code, linkedIncident service
])
  .then(_ => ({ linked: true }))
  .catch(e => ({ error: ['Can not Update Jira Issue Properties.', e] }));

const putIncidentIssueMappingIntoService = async (issue, incidentId) => {
  try {
    const accMapping = await integration.getAccountMapping();
    if (accMapping) {
      await integration.createIncidentIssueMapping(incidentId, issue.key);
      return { linked: true };
    } else {
      return { linked: false };
    }
  } catch (error) {
    return { error };
  }
};

const CreateIncidentFormWithFormik = withFormik({
  mapPropsToValues: ({ issue, jira: { context } }) => {
    const {
      summary,
      description: issueDescriptionFallback,
      reporter,
      issuetype,
      status,
      labels,
      components,
    } = (issue && issue.fields) || {};

    const {
      description: htmlDescription
    } = (issue && issue.renderedFields) || {};

    const jiraDescription = stripHtml(htmlDescription)
      || issueDescriptionFallback
      || '';

    const prefix = `${issue.key}: `;
    const title = `${prefix}${summary || ''}`;

    const description = [
      `Jira URL: ${context.browseBaseUrl}/browse/${issue.key}`,
      reporter && reporter.displayName && `Reporter: ${reporter.displayName}`,
      issuetype && issuetype.name && `Issue type: ${issuetype.name}`,
      status && status.name && `Status: ${status.name}`,
      labels && labels.length > 0 && `Labels: ${labels.join(', ')}`,
      components && components.length > 0 && `Component/s: ${components.map(c => c.name || '').join(', ')}`,
      jiraDescription && `Description: ${jiraDescription.replace(/(\r?\n){2}/g, '\n')}`,
    ]
      .filter(x => x)
      .join('\n');

    return {
      title,
      description,
      service: null,
    };
  },

  validate: values => {
    const errors = {};
    if (!values.title) {
      errors.title = 'Incident title is required';
    }

    if (!values.service) {
      errors.service = 'Service is required';
    }

    return errors;
  },

  handleSubmit: async (
    { title, description, service },
    {
      props: {
        issue,
        jira: { context, session }
      },
      setSubmitting,
      setStatus
    }) => {
    const user = { ...session };
    delete user.loginInfo;

    let shouldClose = true;

    try {
      trackEvent(analyticsEvent, { label: 'new_incident' });
      const incidentId = await createIncident(service, title, description, issue, user);

      const results = await Promise.all([
        putIncidentIdIntoIssueProps(issue, incidentId),
        putIncidentIssueMappingIntoService(issue, incidentId)
      ]);
      const errors = results.map(r => r && r.error).filter(e => e);

      if (errors.length !== 0) {
        const details = errors.map(e => textifyError(e)).join('. ');

        const errorMessage = results.some(r => r && r.linked)
          ? 'Incident is linked, but some errors occurred. Further synchronisation might suffer.'
          : 'Failed to link incident. Incident is created but further synchronisation might suffer.';

        setStatus({ warning: [errorMessage, details] });
        shouldClose = false;
      }
    } catch (e) {
      setStatus({ error: ['Failed to Create Incident', e] });
      shouldClose = false;
    }

    setSubmitting(false);
    if (shouldClose) jira.closeCurrentModal();
  },

})(CreateIncidentForm);

export default CreateIncidentFormWithFormik;
